import type { ThemeDefinition } from '../types';

/**
 * Graphite & Azure — from "CentraOne Graphite Azure · Theme Guide" (v1).
 *
 * Zinc-neutral surfaces, one azure accent, nothing else coloured. The guide's
 * strongest opinion is that colour is reserved for MEANING: azure means
 * interactive, four hues mean four states, and every other pixel is a grey.
 * That is what makes a single burgundy chip readable across a dense table, and
 * it is why nothing below invents a fifth hue.
 *
 * Mapping notes — the guide names fourteen tokens and six washes; this product
 * reads fifty. Where the guide is explicit its value is used verbatim. The
 * derivations, and why each one exists:
 *
 *  - `--co-line-strong` — the guide says "there is no second border value".
 *    This codebase has three weights baked into ~3,600 references (card edge,
 *    control edge, hover edge), and collapsing them to one erases the hover
 *    and disabled affordances the guide's own section 8 checklist requires.
 *    #C9CFD8 is a real zinc step between #DFE3E9 and #9AA3B0, not a new hue.
 *
 *  - `--co-ink-4` / `--co-ink-3-soft` — the guide puts its contrast FLOOR at
 *    ink-3 (#656E7C) and says nothing lighter carries text. Both of these
 *    tokens carry text here (mono labels, placeholders), so neither goes past
 *    the floor: #6B7481 is the guide's own `resolved` value and still clears
 *    4.5:1 on white. The product loses one step of grey hierarchy; it does not
 *    lose a contrast check.
 *
 *  - `--co-machine-*` — the guide deliberately has no machine hue (section 4):
 *    azure carries both "interactive" and "machine output", separated by SHAPE
 *    rather than colour. So the machine family resolves to the accent family,
 *    which is exactly the accent-soft block plus AI badge it prescribes.
 *
 *  - Faces — the guide sets Instrument Sans + JetBrains Mono. JetBrains Mono is
 *    already self-hosted here; Instrument Sans is not, and this app makes no
 *    third-party font request on purpose (an authenticated console must not
 *    FOUT behind a blocked CDN). So the text and display roles use the
 *    self-hosted IBM Plex Sans, the product-wide text face. Adopting Instrument
 *    Sans means dropping `instrument-sans-latin{,-ext}.woff2` into
 *    `public/fonts`, adding the two @font-face blocks, and changing the two
 *    lines below.
 *
 *  - `--co-brass-*` — the token names are historical (see ThemePalette). The
 *    values are the guide's azure: accent #2A62C4, hover #1E4A99, label #FFF.
 *    White on #2A62C4 is 5.43:1, so unlike the brass ramp the accent itself is
 *    the fill and no darker "ink" step is needed.
 *
 *  - The guide's dark-mode warning is honoured: the accent's label is dark ink
 *    on the light-blue dark accent (#10141A on #6E9BE8, about 8.9:1), never
 *    white, which would be 2.8:1 on the most important control on the screen.
 *
 *  - The one-token-two-roles trap is honoured: `--co-ink` is the first chart
 *    series, and the ink band is `--co-neutral-band`. In the dark ramp the band
 *    moves UP (#1A2029, a raised panel) while `--co-ink` moves to near-white,
 *    so a bar stays maximally far from its card instead of a step away from it.
 */
export const graphiteAzure: ThemeDefinition = {
  id: 'graphite-azure',
  name: 'Graphite & Azure',
  description: 'Zinc-neutral surfaces with a single azure accent and softer, rounder geometry.',

  // One ramp per family spanning BOTH appearances, the same way the brass
  // ramps do: `bg-gray-50` is the light canvas and `dark:bg-gray-900` is the
  // dark one, so the 50-950 run has to cover paper at one end and ink at the
  // other.
  palette: {
    // azure ramp
    primary: {
      50: '#EEF3FC', 100: '#DCE7F8', 200: '#BFD3F0', 300: '#9CB9E6', 400: '#6E9BE8',
      500: '#3B72CE', 600: '#2A62C4', 700: '#1E4A99', 800: '#17376F', 900: '#11264A',
    },
    // zinc ramp — the neutral the guide calls for
    secondary: {
      50: '#FFFFFF', 100: '#F6F7F9', 200: '#EFF1F4', 300: '#DFE3E9', 400: '#9AA3B0',
      500: '#656E7C', 600: '#59616E', 700: '#2A323D', 800: '#10141A', 900: '#0B0E12',
    },
    gray: {
      50: '#F6F7F9', 100: '#EFF1F4', 200: '#DFE3E9', 300: '#C9CFD8', 400: '#9AA3B0',
      500: '#656E7C', 600: '#59616E', 700: '#2A323D', 800: '#161C24', 900: '#10141A',
      950: '#0B0E12',
    },
    brass: {
      DEFAULT: '#2A62C4', ink: '#1E4A99', light: '#6E9BE8', pale: '#BFD3F0',
    },
    signal: {
      ok: '#17795E', okSoft: '#3FA383',
      warn: '#A55A06', warnSoft: '#D98A2B',
      crit: '#B23A2B', critSoft: '#E0685A',
    },
  },

  base: {
    // See the faces note above — self-hosted only.
    '--co-font-display': "'IBM Plex Sans', system-ui, sans-serif",
    '--co-font-text': "'IBM Plex Sans', system-ui, sans-serif",
    '--co-font-mono': "'JetBrains Mono', ui-monospace, monospace",
    '--co-font-sans': 'var(--co-font-text)',

    // Geometry: the guide's 10px radius / 6px radius-sm, with the in-between
    // steps filled so every Tailwind radius utility lands on a value from the
    // same scale rather than snapping back to a legacy 2px.
    '--co-radius': '6px',
    '--co-radius-sm': '4px',
    '--co-radius-md': '6px',
    '--co-radius-lg': '8px',
    '--co-radius-xl': '10px',
    '--co-radius-2xl': '12px',
    '--co-radius-card': '10px',

    // Motion is not in the guide; keep the product's cadence so a theme swap
    // changes how the app looks and never how it feels to use.
    '--co-dur-fast': '120ms',
    '--co-dur': '180ms',
    '--co-ease': 'cubic-bezier(.2, .6, .3, 1)',
  },

  light: {
    '--co-canvas': '#F6F7F9',
    '--co-surface': '#FFFFFF',
    '--co-page': '#FFFFFF',
    // Cards, popovers and controls share the one white ground; the guide has
    // no step above surface, so `raised` is surface.
    '--co-raised': '#FFFFFF',
    '--co-rail': '#EFF1F4',
    '--co-sunken': '#EFF1F4',
    '--co-line': '#DFE3E9',
    '--co-line-strong': '#C9CFD8',
    '--co-line-subtle': '#EFF1F4',
    '--co-line-frame': '#DFE3E9',
    '--co-ink': '#10141A',
    '--co-ink-2': '#59616E',
    '--co-ink-3': '#656E7C',
    '--co-ink-3-soft': '#6B7481',
    '--co-ink-4': '#6B7481',
    '--co-ink-invert': '#FFFFFF',
    // The neutral identity square. Ink in light; its own token because in the
    // dark ramp `--co-ink` is near-white and a filled avatar became a blank
    // white tile. Initials on it are always `--co-ink-invert`.
    '--co-avatar': '#10141A',
    '--co-selected': '#EEF3FC',
    '--co-brass': '#2A62C4',
    '--co-brass-ink': '#2A62C4',
    '--co-brass-hover': '#1E4A99',
    '--co-brass-wash': 'rgba(42, 98, 196, .08)',
    '--co-brass-on': '#FFFFFF',
    '--co-green': '#17795E',
    '--co-green-soft': '#3FA383',
    '--co-green-wash': 'rgba(23, 121, 94, .10)',
    '--co-terracotta': '#A55A06',
    '--co-terracotta-soft': '#D98A2B',
    '--co-terracotta-wash': 'rgba(165, 90, 6, .10)',
    '--co-burgundy': '#B23A2B',
    '--co-neutral-band': '#EFF1F4',
    '--co-green-line': '#A5C9BE',
    '--co-green-ink': '#17795E',
    '--co-terracotta-line': '#E3C69F',
    '--co-terracotta-ink': '#A55A06',
    '--co-burgundy-wash': 'rgba(178, 58, 43, .10)',
    '--co-machine': '#2A62C4',
    '--co-machine-ink': '#1E4A99',
    '--co-machine-line': '#BFD3F0',
    '--co-machine-wash': '#EEF3FC',
    '--co-burgundy-line': '#E4B8B1',
    '--co-brass-line': '#BFD3F0',
    // Text and rules that sit ON an ink band (the guide's bubble, #1A2029).
    '--co-on-ink': '#FFFFFF',
    '--co-on-ink-2': '#C3C9D2',
    '--co-on-ink-line': '#2F3742',
    // The "-pale" ramp is for figures sitting on that dark band ONLY; the
    // paper-weight tokens fail contrast there.
    '--co-brass-pale': '#8FB2EE',
    '--co-burgundy-pale': '#E0685A',
    '--co-burgundy-pale-ink': '#E4B8B1',
    '--co-machine-pale': '#8FB2EE',
    '--co-shadow': '0 1px 2px rgba(16, 20, 26, .04)',
    '--co-shadow-pop': '0 10px 26px rgba(16, 20, 26, .16)',
    '--co-shadow-card': '0 1px 2px rgba(16, 20, 26, .04), 0 6px 20px rgba(16, 20, 26, .06)',
    'color-scheme': 'light',
  },

  dark: {
    // Derived, not inverted. A raised thing stays lighter than its ground in
    // both ramps, the status hues are re-picked at dark-legible lightness
    // rather than lightened, and the accent's label flips to ink.
    '--co-canvas': '#0B0E12',
    '--co-surface': '#161C24',
    '--co-page': '#161C24',
    '--co-raised': '#1A2029',
    '--co-rail': '#1A2029',
    '--co-sunken': '#10141A',
    '--co-line': '#2A323D',
    '--co-line-strong': '#3A434F',
    '--co-line-subtle': '#222933',
    '--co-line-frame': '#2A323D',
    '--co-ink': '#F6F7F9',
    '--co-ink-2': '#DFE3E9',
    '--co-ink-3': '#9AA3B0',
    '--co-ink-3-soft': '#98A1AE',
    '--co-ink-4': '#9AA3B0',
    '--co-ink-invert': '#161C24',
    '--co-avatar': '#9AA3B0',
    '--co-selected': '#1B2740',
    '--co-brass': '#6E9BE8',
    '--co-brass-ink': '#6E9BE8',
    '--co-brass-hover': '#8FB2EE',
    '--co-brass-wash': 'rgba(110, 155, 232, .12)',
    // NOT white. Dark ink on the light-blue accent.
    '--co-brass-on': '#10141A',
    '--co-green': '#3FA383',
    '--co-green-soft': '#3FA383',
    '--co-green-wash': 'rgba(63, 163, 131, .12)',
    '--co-terracotta': '#D98A2B',
    '--co-terracotta-soft': '#D98A2B',
    '--co-terracotta-wash': 'rgba(217, 138, 43, .12)',
    '--co-burgundy': '#E0685A',
    '--co-neutral-band': '#1A2029',
    '--co-green-line': '#2C5A4A',
    '--co-green-ink': '#3FA383',
    '--co-terracotta-line': '#5C4A2A',
    '--co-terracotta-ink': '#D98A2B',
    '--co-burgundy-wash': 'rgba(224, 104, 90, .14)',
    '--co-machine': '#6E9BE8',
    '--co-machine-ink': '#8FB2EE',
    '--co-machine-line': '#33436B',
    '--co-machine-wash': '#1B2740',
    '--co-burgundy-line': '#5E332C',
    '--co-brass-line': '#33436B',
    // `--co-ink` is near-white in this ramp, so an "ink band" becomes a LIGHT
    // band and everything sitting on it inverts with it.
    '--co-on-ink': '#10141A',
    '--co-on-ink-2': '#4A525E',
    '--co-on-ink-line': '#C9CFD8',
    '--co-brass-pale': '#33436B',
    '--co-burgundy-pale': '#B23A2B',
    '--co-burgundy-pale-ink': '#8E3427',
    '--co-machine-pale': '#33436B',
    '--co-shadow': '0 1px 2px rgba(0, 0, 0, .34)',
    '--co-shadow-pop': '0 12px 32px rgba(0, 0, 0, .5)',
    '--co-shadow-card': '0 1px 2px rgba(0, 0, 0, .34), 0 6px 20px rgba(0, 0, 0, .46)',
    'color-scheme': 'dark',
  },

  preview: {
    light: ['#F6F7F9', '#FFFFFF', '#2A62C4', '#10141A'],
    dark: ['#0B0E12', '#1A2029', '#6E9BE8', '#F6F7F9'],
  },
};
